// Экран редактора: верхняя строка (панель быстрого доступа, имя отчёта, сохранение, поиск команд, проверка,
// экспорт), лента, плашки, панели, лист, строка состояния; «Файл» (бэкстейдж), режим «Фокус», F6 - по областям.
import { useEffect } from 'react';
import { useApp } from '../core/store';
import { ctx, ctxFor, runCommand } from '../core/ctx';
import { closeProject } from '../core/project';
import { registry } from '../core/registry';
import { Ribbon } from './Ribbon';
import { EditorPage } from './EditorPage';
import { PanelHost, PanelRail } from './Panels';
import { StatusBar } from './StatusBar';
import { Backstage } from './Backstage';
import { Btn, prettyShortcut } from './controls';
import { Icon } from './icons';
import { useUi } from './uiState';
import { Banners } from '../features/shell/backup';
import { TourCard } from '../features/shell/help';
import { useEditorTick } from './hooks';
import { AccountMenu, ForeignBanner } from '../features/auth/account';
import { ReadOnlyBanner } from '../features/auth/billing';
import { CancelJobButton } from '../features/auth/jobs';
import { backHash } from '../features/auth/session';

const SAVE_TEXT: Record<string, string> = {
  saved: 'Сохранено', dirty: 'Есть изменения…', saving: 'Сохранение…', error: 'Ошибка сохранения', conflict: 'Конфликт версий',
};
const TYPE_SHORT: Record<string, string> = {
  lab: 'Лабораторная', pr: 'Практическая', kr: 'Контрольная', rgr: 'РГР', referat: 'Реферат', essay: 'Эссе',
  kursovaya: 'Курсовая работа', kp: 'Курсовой проект', practice: 'Практика', sheet: 'Чистый лист',
};

/** Кнопка панели быстрого доступа (значок команды, подсказка с клавишами). */
function Qat({ id, icon }: { id: string; icon?: string }) {
  useEditorTick();
  const c = registry.commands.get(id);
  if (!c) return null;
  const enabled = c.isEnabled ? c.isEnabled(ctxFor(id)) : true;
  return (
    <button type="button" className="qat-btn" disabled={!enabled} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand(id)}
      data-tip={c.title} data-tip-desc={c.description} data-tip-key={c.shortcut ? prettyShortcut(c.shortcut) : undefined}
      aria-label={c.title} data-testid={'qat-' + id}>
      <Icon name={icon || c.icon} size={16} />
    </button>
  );
}

const REGIONS = ['.ribbon-tab.is-active', '.sto-doc', '.panel-left .panel-body', '.panel-right .panel-body', '.statusbar button, .statusbar input'];
function cycleRegion(back: boolean) {
  const found = REGIONS.map((s) => document.querySelector(s) as HTMLElement | null).filter((x): x is HTMLElement => !!x && x.offsetParent !== null);
  if (!found.length) return;
  const i = found.findIndex((el) => el.contains(document.activeElement));
  const next = found[(i + (back ? -1 : 1) + found.length) % found.length];
  if (next.matches('.panel-body')) {
    const f = next.querySelector('button, input, [tabindex]') as HTMLElement | null;
    (f || next).focus();
  } else next.focus();
}

/** «В очереди Word: перед вами 2» -> «в очереди, перед вами 2» (для узкой верхней строки). */
function queueShort(p?: string): string {
  const m = /очереди Word: перед вами (\d+)/.exec(p || '');
  return m ? `в очереди, перед вами ${m[1]}` : '';
}

export function Shell() {
  const pid = useApp((s) => s.projectId);
  const meta = useApp((s) => s.meta);
  const save = useApp((s) => s.save);
  const check = useApp((s) => s.check);
  const focus = useUi((s) => s.focusMode);
  const task = useUi((s) => s.task);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'F6') { e.preventDefault(); cycleRegion(e.shiftKey); }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  const busy = check.running ? check.progress || 'Проверка…' : task ? `${task.label}: ${task.progress || '…'}` : '';
  const busyShort = check.running ? queueShort(check.progress) || busy : task ? `${task.short || task.label}: ${queueShort(task.progress) || task.progress || '…'}` : '';
  return (
    <div className={'shell' + (focus ? ' is-focus' : '')}>
      <header className="topbar">
        <button type="button" className="topbar-back" data-tip="К списку отчётов" data-testid="back" aria-label="К списку отчётов"
          onClick={() => { const to = backHash(); void closeProject().then(() => { location.hash = to; }); }}>
          <Icon name="back" size={16} /><span className="logo">СТО</span>
        </button>
        <div className="qat" role="toolbar" aria-label="Панель быстрого доступа">
          <Qat id="file.save" />
          <Qat id="edit.undo" />
          <Qat id="edit.redo" />
        </div>
        <div className="topbar-doc">
          <button type="button" className="topbar-title" data-tip="Переименовать отчёт" onClick={() => runCommand('project.rename')} data-testid="doc-title">{pid?.replace(/^u\d+:/, '')}</button>
          {meta && <span className="topbar-type">{TYPE_SHORT[meta.type] || meta.type}{meta.number ? ` № ${meta.number}` : ''}</span>}
        </div>
        <span className={'save-state save-' + save.state} data-testid="save-state" data-tip={save.error || (save.at ? 'Сохранено: ' + new Date(save.at).toLocaleTimeString('ru-RU') : '')}>
          <Icon name={save.state === 'saved' ? 'ok' : save.state === 'error' || save.state === 'conflict' ? 'warn' : 'spinner'} size={13}
            className={save.state === 'saving' ? 'spin' : ''} />
          {save.code === 'project_deleted' ? 'Отчёт удалён' : SAVE_TEXT[save.state]}
        </span>
        <span className="topbar-spacer" />
        <button type="button" className="topbar-search" onClick={() => runCommand('app.palette')} data-testid="top-search"
          data-tip="Командная палитра" data-tip-desc="Любая команда или раздел документа" data-tip-key="Ctrl+K">
          <Icon name="search" size={14} /><span>Поиск команд и разделов</span><kbd>Ctrl+K</kbd>
        </button>
        <span className="topbar-spacer" />
        {busy && <span className="topbar-progress" data-testid="top-progress" title={busy} data-full={busy}><Icon name="spinner" className="spin" /> {busyShort}</span>}
        {busy && <CancelJobButton pid={pid} />}
        <Btn icon="check" label="Проверить по СТО" onClick={() => runCommand('sto.check')} disabled={check.running} testid="top-check"
          shortcut="F7" desc="Собрать отчёт в Word и проверить все требования СТО" />
        <Btn icon="docx" label="DOCX" onClick={() => runCommand('file.exportDocx')} testid="top-docx" desc="Собрать и скачать .docx" disabled={!!task} />
        <Btn icon="pdf" label="PDF" onClick={() => runCommand('file.exportPdf')} testid="top-pdf" desc="Собрать и скачать PDF с закладками" disabled={!!task} />
        <Btn icon="theme" title="Тёмная / светлая тема" onClick={() => runCommand('view.theme')} testid="top-theme" className="topbar-icon" />
        <Btn icon="help" title="Справка" desc="Памятка по СТО и горячие клавиши" shortcut="F1" onClick={() => runCommand('app.help')} testid="top-help" className="topbar-icon" />
        <AccountMenu compact />
      </header>
      <Ribbon />
      <Banners />
      <ForeignBanner />
      <ReadOnlyBanner />
      <div className="workspace">
        <PanelRail side="left" />
        <PanelHost side="left" />
        <EditorPage />
        <PanelHost side="right" />
        <PanelRail side="right" />
      </div>
      <StatusBar />
      <Backstage />
      <TourCard />
    </div>
  );
}
